import { graphql } from 'gatsby';
import React, { useEffect } from 'react';
import { useState } from 'react';
import Submit from '../../fragments/submit';
import { blindPostRequest, hop } from '../../../utils/network';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { preloadImage } from '~/utils/helpers';

const HelpfulState = {
  Initial: 'initial',
  Good: 'good',
  Bad: 'bad',
};

const getVotesUrl = (id: string) =>
  `${process.env.GATSBY_ACADEMY_API_HOST}/api/article/${id}/votes`;

const fetchVotes = async (url: string, callback: React.Dispatch<any>) => {
  await hop({
    url,
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${process.env.GATSBY_ACADEMY_API_KEY}`,
    },
  }).then(response => {
    if (response && response.data) {
      const { votes } = response.data;
      callback({
        useful: votes.useful,
        total: votes.total,
      });
    }
  });
};

const Helpful = ({ block, data }: any) => {
  const [votes, setVotes] = useState({
    useful: 0,
    total: 0,
  });
  const [parent] = useAutoAnimate<HTMLDivElement>();
  const [cardState, setCardState] = useState(HelpfulState.Initial);

  const thankYouCallback = () => {
    setCardState(HelpfulState.Good);
  };

  useEffect(() => {
    const votesUrl = getVotesUrl(data.Id);
    fetchVotes(votesUrl, setVotes);

    // preload images
    preloadImage(block.initial.yes.image);
    preloadImage(block.initial.no.image);
    preloadImage(block.thank_you.image);
  }, []);

  const clickHandler = (helpfulState: string) => {
    const addVoteUrl = `${getVotesUrl(data.Id)}/${helpfulState}`;

    hop({
      url: addVoteUrl,
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${process.env.GATSBY_ACADEMY_API_KEY}`,
      },
    }).then(response => {
      if (response) {
        setCardState(helpfulState);
        setVotes({
          useful:
            helpfulState === HelpfulState.Good
              ? votes.useful + 1
              : votes.useful,
          total: votes.total + 1,
        });
      }
    });
  };

  return (
    <section
      ref={parent}
      className={`block__helpful block ${block._template} ${block.css_class_name}`}
    >
      {cardState === HelpfulState.Initial && (
        <HelpfulInitialCard
          props={block.initial}
          clickHandler={clickHandler}
          votes={votes}
        />
      )}
      {cardState === HelpfulState.Bad && (
        <HelpfulSorryCard
          data={block.sorry}
          submitCallback={thankYouCallback}
        />
      )}
      {cardState === HelpfulState.Good && (
        <HelpfulThankYouCard props={block.thank_you} />
      )}
    </section>
  );
};

/**
 * Helper components
 * */
const HelpfulSorryCard = ({
  data,
  submitCallback,
}: {
  data: {
    title: string;
    placeholder_text: string;
    cta_text: string;
    submit_url: string;
  };
  submitCallback: () => void;
}) => {
  const [message, setMessage] = useState('');
  const [isPending, setIsPending] = useState(false);

  const formSubmitHandler = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isPending) return;
    setIsPending(true);
    const source =
      typeof window !== 'undefined' ? window.location.href : 'none';
    await blindPostRequest(data.submit_url, {
      ContactEmail: 'article.not.helpful.form@bunny.net',
      IsValid: 'false',
      FullName: 'Article Not Helpful Form',
      Message: `Source: ${source} - ${message}`,
    });
    submitCallback();
  };

  return (
    <>
      <form
        onSubmit={formSubmitHandler}
        id='cf-hsc'
      >
        <h4
          className='helpful-title'
          dangerouslySetInnerHTML={{ __html: data.title }}
        />
        <textarea
          name='Message'
          id='Message'
          className='helpful-textarea'
          required
          rows={5}
          minLength={10}
          placeholder={data.placeholder_text}
          value={message}
          onChange={event => setMessage(event.target.value)}
        />
        <div className='col-12'>
          <Submit wrapperStyle='helpful-cta helpful-cta--soft'>
            {data.cta_text}
          </Submit>
        </div>
      </form>
    </>
  );
};

const HelpfulThankYouCard = ({ props }: any) => {
  return (
    <>
      <h4 className='helpful-title'>{props.title}</h4>
      <img
        className='helpful-image min-h-[14.4rem]'
        src={props.image}
        alt={props.image_alt_text}
        loading='eager'
      />
    </>
  );
};

const HelpfulInitialCard = ({ props, clickHandler, votes }: any) => {
  return (
    <>
      <h4 className='helpful-title'>{props.title}</h4>
      <div className='helpful-options'>
        <div className='helpful-options__option'>
          <button
            className='helpful-option__cta'
            type='button'
            onClick={() => clickHandler(HelpfulState.Good)}
          >
            <img
              src={props.yes.image}
              alt={props.yes.image_alt_text}
              className='min-h-[6.6rem]'
              loading='eager'
            />
            <span>{props.yes.text}</span>
          </button>
        </div>
        <div className='helpful-options__option'>
          <button
            className='helpful-option__cta'
            type='button'
            onClick={() => clickHandler(HelpfulState.Bad)}
          >
            <img
              src={props.no.image}
              alt={props.no.image_alt_text}
              className='min-h-[6.6rem]'
              loading='eager'
            />
            <span>{props.no.text}</span>
          </button>
        </div>
      </div>
      <p className='helpful-votes'>
        <span className='bold'>{votes.useful}</span> out of{' '}
        <span className='bold'>{votes.total}</span> Bunnies found this article
        helpful
      </p>
    </>
  );
};

/**
 * Exports
 */
export default Helpful;

export const helpfulQuery = graphql`
  fragment HelpfulFragment on Block {
    settings {
      margin_bottom
      css_class_name
      custom_css
    }
    form_style
    column_select
    initial {
      title
      yes {
        text
        image_alt_text
        image
      }
      no {
        text
        image_alt_text
        image
      }
    }
    thank_you {
      title
      image_alt_text
      image
    }
    sorry {
      title
      placeholder_text
      cta_text
      submit_url
    }
  }
`;
